'use client';

import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { optionChipClass } from '@/lib/option-styles';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { ModuleFormField, ModuleFormSection } from '@/types/form-template';

export function isOtherValue(val: string): boolean {
  return val.startsWith('other:');
}

export function getOtherText(val: string): string {
  return val.slice(6);
}

export function validateFormFields(
  fields: ModuleFormField[],
  values: Record<string, unknown>,
  fieldIds?: string[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (!field.enabled || !field.required) continue;
    if (fieldIds && !fieldIds.includes(field.id)) continue;
    const value = values[field.id];

    const basicEmpty =
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0);

    let isEmpty = basicEmpty;

    if (!isEmpty) {
      if (typeof value === 'string' && isOtherValue(value)) {
        if (getOtherText(value).trim().length === 0) isEmpty = true;
      }
      if (Array.isArray(value) && value.length > 0) {
        const nonOther = value.filter((v) => !isOtherValue(String(v)));
        if (nonOther.length === 0) {
          const anyOtherWithText = value.some(
            (v) => getOtherText(String(v)).trim().length > 0,
          );
          if (!anyOtherWithText) isEmpty = true;
        }
      }
    }

    if (isEmpty) {
      errors[field.id] = `${field.label} is required`;
    }
  }
  return errors;
}

export function renderFormField(
  field: ModuleFormField,
  value: unknown,
  onChange: (fieldId: string, value: unknown) => void,
  disabled: boolean,
  error?: string,
) {
  switch (field.type) {
    case 'checkbox-group':
      return (
        <CheckboxGroupField
          key={field.id}
          field={field}
          value={value as string[] | undefined}
          onChange={onChange}
          disabled={disabled}
          error={error}
        />
      );

    case 'radio':
      return (
        <RadioOptionsField
          key={field.id}
          field={field}
          value={value as string | undefined}
          onChange={onChange}
          disabled={disabled}
          error={error}
        />
      );

    case 'select':
      return field.allowOther ? (
        <SelectWithOtherField
          key={field.id}
          field={field}
          value={value as string | undefined}
          onChange={onChange}
          disabled={disabled}
          error={error}
        />
      ) : (
        <div key={field.id} className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Select
            value={(value as string) || ''}
            onValueChange={(v) => onChange(field.id, v)}
            disabled={disabled}
          >
            <SelectTrigger className={error ? 'border-destructive' : undefined}>
              <SelectValue placeholder={field.placeholder || 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      );

    case 'textarea':
      return (
        <div key={field.id} className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Textarea
            value={(value as string) || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            disabled={disabled}
            className={error ? 'border-destructive' : undefined}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      );

    case 'number':
      return (
        <div key={field.id} className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Input
            type="number"
            step="0.05"
            min={field.validation?.min}
            max={field.validation?.max}
            value={value !== undefined && value !== null ? String(value) : ''}
            onChange={(e) =>
              onChange(field.id, e.target.value ? Number(e.target.value) : null)
            }
            placeholder={field.placeholder}
            disabled={disabled}
            className={cn('max-w-40', error && 'border-destructive')}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      );

    case 'checkbox':
      return (
        <div key={field.id} className="space-y-1">
          <div className="flex items-center gap-2">
            <Checkbox
              id={field.id}
              checked={!!value}
              onCheckedChange={(checked) => onChange(field.id, checked)}
              disabled={disabled}
            />
            <Label htmlFor={field.id}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      );

    case 'date':
      return (
        <div key={field.id} className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <DatePicker
            value={value ? parseISO(value as string) : undefined}
            onChange={(d) =>
              onChange(field.id, d ? format(d, 'yyyy-MM-dd') : '')
            }
            disabled={disabled}
            clearable
            showDropdowns
            className={cn('max-w-52', error && 'border-destructive')}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      );

    default:
      return (
        <div key={field.id} className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Input
            type={field.type === 'email' ? 'email' : 'text'}
            value={(value as string) || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={cn('max-w-sm', error && 'border-destructive')}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      );
  }
}

function CheckboxGroupField({
  field,
  value,
  onChange,
  disabled,
  error,
}: {
  field: ModuleFormField;
  value: string[] | undefined;
  onChange: (fieldId: string, value: unknown) => void;
  disabled: boolean;
  error?: string;
}) {
  const selected: string[] = Array.isArray(value)
    ? value.map((v) => String(v))
    : value == null
      ? []
      : [String(value)];
  const selectedStrings = selected.map(String);
  const hasOther = selectedStrings.some((v) => isOtherValue(v));
  const otherText = hasOther
    ? getOtherText(selectedStrings.find((v) => isOtherValue(v)) || '')
    : '';

  const toggleOption = (optionValue: string) => {
    const newSelected = selectedStrings.includes(optionValue)
      ? selected.filter((v) => String(v) !== optionValue)
      : [...selected, optionValue];
    onChange(field.id, newSelected);
  };

  const toggleOther = () => {
    if (hasOther) {
      onChange(
        field.id,
        selected.filter((v) => !isOtherValue(String(v))),
      );
    } else {
      onChange(field.id, [...selected, 'other:']);
    }
  };

  const updateOtherText = (text: string) => {
    const withoutOther = selected.filter((v) => !isOtherValue(String(v)));
    onChange(field.id, [...withoutOther, `other:${text}`]);
  };

  return (
    <div className="space-y-2">
      <Label>
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <div role="group" className="flex flex-wrap gap-2">
        {(field.options || []).map((opt) => {
          const active = selectedStrings.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              role="checkbox"
              aria-checked={active}
              disabled={disabled}
              onClick={() => toggleOption(opt.value)}
              className={cn(
                optionChipClass(active, { invalid: !!error, disabled }),
                'max-w-full gap-1.5 text-left',
              )}
            >
              {active && <Check className="h-3.5 w-3.5 shrink-0" />}
              <span className="break-words">{opt.label}</span>
            </button>
          );
        })}
        {field.allowOther && (
          <button
            type="button"
            role="checkbox"
            aria-checked={hasOther}
            disabled={disabled}
            onClick={toggleOther}
            className={cn(
              optionChipClass(hasOther, { invalid: !!error, disabled }),
              'gap-1.5',
            )}
          >
            {hasOther && <Check className="h-3.5 w-3.5 shrink-0" />}
            Other
          </button>
        )}
      </div>
      {hasOther && (
        <Input
          value={otherText}
          onChange={(e) => updateOtherText(e.target.value)}
          placeholder="Please specify..."
          className="max-w-sm"
          disabled={disabled}
        />
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function SelectWithOtherField({
  field,
  value,
  onChange,
  disabled,
  error,
}: {
  field: ModuleFormField;
  value: string | undefined;
  onChange: (fieldId: string, value: unknown) => void;
  disabled: boolean;
  error?: string;
}) {
  const currentValue = value || '';
  const isOther = isOtherValue(currentValue);
  const [otherText, setOtherText] = useState(
    isOther ? getOtherText(currentValue) : '',
  );

  const handleChange = (v: string) => {
    if (v === '__other__') {
      onChange(field.id, `other:${otherText}`);
    } else {
      onChange(field.id, v);
      setOtherText('');
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    onChange(field.id, `other:${text}`);
  };

  return (
    <div className="space-y-2">
      <Label>
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <Select
        value={isOther ? '__other__' : currentValue}
        onValueChange={handleChange}
        disabled={disabled}
      >
        <SelectTrigger className={error ? 'border-destructive' : undefined}>
          <SelectValue placeholder={field.placeholder || 'Select...'} />
        </SelectTrigger>
        <SelectContent>
          {(field.options || []).map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
          <SelectItem value="__other__">Other</SelectItem>
        </SelectContent>
      </Select>
      {isOther && (
        <Input
          value={otherText}
          onChange={(e) => handleOtherTextChange(e.target.value)}
          placeholder="Please specify..."
          disabled={disabled}
        />
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function RadioOptionsField({
  field,
  value,
  onChange,
  disabled,
  error,
}: {
  field: ModuleFormField;
  value: string | undefined;
  onChange: (fieldId: string, value: unknown) => void;
  disabled: boolean;
  error?: string;
}) {
  const currentValue = value || '';
  const isOther = isOtherValue(currentValue);
  const chipClass = (active: boolean) =>
    optionChipClass(active, { invalid: !!error, disabled });

  return (
    <div className="space-y-2">
      <Label>
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <div role="radiogroup" className="flex flex-wrap gap-2">
        {(field.options || []).map((opt) => (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={currentValue === opt.value}
            disabled={disabled}
            onClick={() => onChange(field.id, opt.value)}
            className={chipClass(currentValue === opt.value)}
          >
            {opt.label}
          </button>
        ))}
        {field.allowOther && (
          <button
            type="button"
            role="radio"
            aria-checked={isOther}
            disabled={disabled}
            onClick={() => onChange(field.id, 'other:')}
            className={chipClass(isOther)}
          >
            Other
          </button>
        )}
      </div>
      {isOther && (
        <Input
          value={getOtherText(currentValue)}
          onChange={(e) => onChange(field.id, `other:${e.target.value}`)}
          placeholder="Please specify..."
          disabled={disabled}
          className="max-w-sm"
        />
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

interface DynamicFormRendererProps {
  fields: ModuleFormField[];
  sections: ModuleFormSection[];
  values: Record<string, unknown>;
  onChange: (fieldId: string, value: unknown) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
  lockedFieldIds?: string[];
}

export function DynamicFormRenderer({
  fields,
  sections,
  values,
  onChange,
  disabled = false,
  errors,
  lockedFieldIds,
}: DynamicFormRendererProps) {
  const enabledFields = fields.filter((f) => f.enabled !== false);
  const fieldMap = new Map(enabledFields.map((f) => [f.id, f]));

  const assignedFieldIds = new Set(sections.flatMap((s) => s.fieldIds));
  const unassignedFields = enabledFields.filter(
    (f) => !assignedFieldIds.has(f.id),
  );

  const renderSection = (section: ModuleFormSection) => {
    const sectionFields = section.fieldIds
      .map((id) => fieldMap.get(id))
      .filter(Boolean) as ModuleFormField[];

    if (sectionFields.length === 0) return null;

    return (
      <Card key={section.id}>
        <CardHeader>
          <CardTitle>{section.title}</CardTitle>
          {section.description && (
            <CardDescription>{section.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {sectionFields.map((field) => (
            <div key={field.id} data-field-id={field.id}>
              {renderFormField(
                field,
                values[field.id],
                onChange,
                disabled || !!lockedFieldIds?.includes(field.id),
                errors?.[field.id],
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {sections.map(renderSection)}

      {unassignedFields.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {unassignedFields.map((field) => (
              <div key={field.id} data-field-id={field.id}>
                {renderFormField(
                  field,
                  values[field.id],
                  onChange,
                  disabled || !!lockedFieldIds?.includes(field.id),
                  errors?.[field.id],
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
